import { useEffect, useState } from 'react';
import { Bell, Plus, X } from 'phosphor-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useActiveCenter } from '@/hooks/useActiveCenter';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Chip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { Toggle } from '@/components/ui/Toggle';
import { CATEGORIES, CATEGORY_LIST } from '@/constants/categories';
import { geocodeCity } from '@/lib/geocode';
import type { EventKind } from '@/types/database';
import { requestStartupNotifications } from '@/lib/appRuntime';
import { clearPushRegistration } from '@/lib/pushNotifications';

type AlertPeriod = 'today' | 'weekend' | 'week' | 'month';
const PERIOD_OPTIONS: Array<{ id: AlertPeriod; label: string }> = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'weekend', label: 'Ce week-end' },
  { id: 'week', label: 'Cette semaine' },
  { id: 'month', label: 'Ce mois' },
];

const RADIUS_OPTIONS: Array<{ label: string; km: number | null }> = [
  { label: '25 km', km: 25 },
  { label: '50 km', km: 50 },
  { label: '100 km', km: 100 },
  { label: 'Partout', km: null },
];

export default function Alerts() {
  const { user, profile, refreshProfile } = useAuth();
  const center = useActiveCenter();
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [city, setCity] = useState('');
  const [kinds, setKinds] = useState<EventKind[]>([]);
  const [radiusKm, setRadiusKm] = useState<number | null>(50);
  const [period, setPeriod] = useState<AlertPeriod>('week');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    if (!user) return;
    const { data } = await supabase.from('alerts').select('*').eq('user_id', user.id).order('created_at', { ascending: false });
    setRows(data ?? []);
  }
  useEffect(() => { load(); }, [user?.id]);

  async function create() {
    if (!user) return;
    setSaving(true);
    setFormError(null);
    // Rayon null = « Partout » (alerte nationale) → pas de centre géo.
    const useGeo = radiusKm != null;

    // Si une ville est saisie, elle devient le CENTRE de l'alerte (géocodée),
    // pas un simple libellé — sinon l'alerte resterait centrée sur la position
    // active alors que l'utilisateur croit surveiller la ville tapée.
    let alertCenter = { lat: center.lat, lon: center.lon, label: center.label };
    if (useGeo && city.trim()) {
      const geo = await geocodeCity(city.trim());
      if (!geo) {
        setSaving(false);
        setFormError('Ville introuvable — vérifiez l’orthographe.');
        return;
      }
      alertCenter = geo;
    }

    const { error } = await supabase.from('alerts').insert({
      user_id: user.id,
      label: city.trim() || (useGeo ? alertCenter.label : null),
      kinds: kinds.length ? kinds : null,
      center_lat: useGeo ? alertCenter.lat : null,
      center_lon: useGeo ? alertCenter.lon : null,
      radius_km: radiusKm,
      period,
      active: true,
    });
    setSaving(false);
    if (error) {
      setFormError("Impossible de créer l'alerte. Réessayez.");
      return;
    }
    setCity(''); setKinds([]); setRadiusKm(50); setPeriod('week'); setOpen(false);
    await load();
    setNotice('Alerte créée.');
    window.setTimeout(() => setNotice(null), 3000);
  }
  async function remove(id: string) {
    await supabase.from('alerts').delete().eq('id', id); load();
  }

  async function activateNotifications() {
    try {
      await requestStartupNotifications(async (token) => {
        if (user) {
          await supabase.from('profiles').update({ push_token: token }).eq('id', user.id);
        }
      });
      if (user) {
        await supabase.from('profiles').update({ notifications_opt_in: true }).eq('id', user.id);
        await refreshProfile();
      }
    } catch {}
  }

  async function toggleNotifications(enabled: boolean) {
    if (!user) return;

    if (!enabled) {
      await clearPushRegistration(user.id);
      await refreshProfile();
      return;
    }

    await activateNotifications();
  }

  const showNotifBanner = profile && !profile.notifications_opt_in;

  return (
    <div className="bg-paper text-ink-1 min-h-screen pb-tabbar">
      <PageHeader back eyebrow="Mes" title="Alertes" italicWord="alertes" />

      {showNotifBanner && (
        <section className="px-5 mb-4">
          <div className="rounded-md bg-terre/8 ring-1 ring-terre/20 p-4 flex items-start gap-3">
            <Bell size={18} className="text-terre mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="t-ui-sm text-ink-1 mb-2">Les notifications sont désactivées. Activez-les pour recevoir vos alertes.</p>
              <Button variant="primary" onClick={() => void activateNotifications()}>Activer les notifications</Button>
            </div>
          </div>
        </section>
      )}

      <section className="px-5 mb-6">
        {profile && (
          <div className="mb-4 rounded-md bg-paper-elev ring-1 ring-paper-edge p-4 flex items-center gap-3">
            <Bell size={18} className="text-terre shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="t-ui">Notifications push</div>
              <div className="t-ui-xs text-ink-3">Active ou coupe totalement l’envoi des alertes sur cet appareil.</div>
            </div>
            <Toggle checked={Boolean(profile.notifications_opt_in)} onChange={(value) => { void toggleNotifications(value); }} />
          </div>
        )}
        <Button full variant={open ? 'secondary' : 'primary'} iconLeft={open ? <X size={16} /> : <Plus size={16} />} onClick={() => setOpen(o => !o)}>
          {open ? 'Annuler' : 'Nouvelle alerte'}
        </Button>
      </section>

      {open && (
        <section className="px-5 mb-8 space-y-4">
          <Input label="Ville (optionnel)" placeholder="Ex. Clermont-l'Hérault" value={city} onChange={e => setCity(e.target.value)} />
          <div>
            <label className="t-cap block mb-2">Catégories</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_LIST.map(c => (
                <Chip key={c.id} active={kinds.includes(c.id)}
                  onClick={() => setKinds(k => k.includes(c.id) ? k.filter(x => x !== c.id) : [...k, c.id])}>
                  {c.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <label className="t-cap block mb-2">Quand</label>
            <div className="flex flex-wrap gap-2">
              {PERIOD_OPTIONS.map(o => (
                <Chip key={o.id} active={period === o.id} onClick={() => setPeriod(o.id)}>
                  {o.label}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <label className="t-cap block mb-2">
              {radiusKm != null ? <>Distance autour de {center.label}</> : 'Zone'}
            </label>
            <div className="flex flex-wrap gap-2">
              {RADIUS_OPTIONS.map(o => (
                <Chip key={o.label} active={radiusKm === o.km} onClick={() => setRadiusKm(o.km)}>
                  {o.label}
                </Chip>
              ))}
            </div>
          </div>
          {formError ? <p className="t-ui-sm text-danger">{formError}</p> : null}
          <Button full onClick={create} loading={saving}>Créer l'alerte</Button>
        </section>
      )}

      {notice && (
        <section className="px-5 mb-4">
          <div className="rounded-md bg-success/10 ring-1 ring-success/30 px-4 py-3 t-ui-sm text-success">{notice}</div>
        </section>
      )}

      <section className="px-5">
        <div className="flex items-center gap-3 mb-3"><GoldenRule /><span className="t-cap whitespace-nowrap text-ink-3">Vos alertes</span><GoldenRule /></div>
        {rows.length === 0 ? (
          <EmptyState icon={<Bell size={28} />} title="Aucune alerte" body="Soyez prévenu·e dès qu'une sortie correspond à vos critères." />
        ) : (
          <ul className="divide-y divide-paper-edge">
            {rows.map(r => (
              <li key={r.id} className="flex items-start gap-3 py-3">
                <Bell size={18} className="text-terre mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="t-ui">{r.label ?? 'Toutes les villes'}{r.radius_km ? ` · ${r.radius_km} km` : ''}</div>
                  <div className="t-ui-xs text-ink-3 mt-0.5">
                    {PERIOD_OPTIONS.find(o => o.id === r.period)?.label ?? 'Cette semaine'}
                    {' · '}
                    {r.kinds?.length ? r.kinds.map((k: EventKind) => CATEGORIES[k]?.label ?? k).join(' · ') : 'Toutes les catégories'}
                  </div>
                </div>
                <button onClick={() => remove(r.id)} className="h-9 w-9 grid place-items-center rounded-full text-ink-3 hover:text-cerise"><X size={16} /></button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
